<script lang="ts" setup>
import { computed, useAttrs } from 'vue';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

defineOptions({
    name: 'IdeoHeading',
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    tag?: HeadingTag;
}>(), {
    tag: 'h2'
});

const attrs = useAttrs();

const sizeClasses: Record<HeadingTag, string> = {
    h1: 'text-ui-h1/[1.25] max-lg:text-[1.375rem]/[1.25] font-[700] tracking-[-0.02em]',
    h2: 'text-ui-h2/[1.25] font-[600] tracking-[-0.01em]',
    h3: 'text-ui-h3/[1.5] font-[600]',
    h4: 'text-ui-h3/[1.5] font-[600]',
    h5: 'text-ui-h3/[1.5] font-[600]',
    h6: 'text-ui-h3/[1.5] font-[600]'
};

function classTokens(value: any): string[]
{
    if (!value)
        return [];

    if (typeof value === 'string')
        return value.split(/\s+/).filter(Boolean);

    if (Array.isArray(value))
        return value.flatMap(item => classTokens(item));

    if (typeof value === 'object')
        return Object.entries(value)
            .filter(([, active]) => active)
            .flatMap(([name]) => classTokens(name));

    return [];
}

const displayOverride = computed(() => classTokens(attrs.class).some(item => /^(?:[\w-]+:)*text-(?:3xl|4xl|5xl|6xl|7xl|8xl|9xl|\[clamp\(|\[(?:[2-9](?:\.\d+)?|\d{2,}(?:\.\d+)?)rem)/.test(item)));

function isOwnedTypographyClass(item: string)
{
    if (displayOverride.value)
        return false;

    return /^(?:[\w-]+:)*(?:text-(?:ui-(?:h[1-3]|body|label|helper|meta|micro)|rich-(?:h[1-6]|body)|\[(?:-?\d*\.?\d+(?:rem|em|px)|clamp\([^\]]+\))\]|xs|sm|base|lg|xl|2xl)(?:\/[^\s]+)?|font-(?:thin|extralight|light|normal|medium|semibold|bold|extrabold|black|\[[^\]]+\])|leading-(?:none|tight|snug|normal|relaxed|loose|\[[^\]]+\])|tracking-(?:tighter|tight|normal|wide|wider|widest|\[[^\]]+\]))$/.test(item);
}

const layoutClasses = computed(() => classTokens(attrs.class).filter(item => !isOwnedTypographyClass(item)));

const rootAttrs = computed(() =>
{
    const result = { ...attrs };

    delete result.class;

    return result;
});

const headingClasses = computed(() => [
    'm-0 text-fg dark:text-fg-dark',
    sizeClasses[props.tag],
    layoutClasses.value
]);
</script>

<template>
    <component :is="props.tag" v-bind="rootAttrs" :class="headingClasses">
        <slot name="default" />
    </component>
</template>
